import { useState, useCallback, useRef } from "react";

/**
 * useDraggable
 *
 * Returns drag state + a pointerdown handler to attach to the drag handle element.
 *
 * Pointer events with capture rather than mouse events on `document`. The previous version added
 * `mousemove` and `mouseup` listeners to the document and removed them on `mouseup` - which never
 * arrived if the button was released over an IFRAME, because that event goes to the iframe's own
 * document. The listeners stayed attached and `dragOrigin` stayed set, so from then on the dialog
 * followed the cursor with no button held: a modal that had a preview iframe in it became impossible
 * to use after one drag that happened to end over the preview.
 *
 * `setPointerCapture` routes every subsequent pointer event for that gesture to the handle regardless
 * of what sits underneath, so the release is always seen. `pointercancel` covers the rest.
 *
 * Usage:
 *   const { position, handlePointerDown, resetPosition } = useDraggable();
 *
 *   <DialogHeader onPointerDown={handlePointerDown} className="tw:cursor-move tw:select-none">
 */
export function useDraggable() {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragOrigin = useRef(null);

  const handlePointerDown = useCallback(
    (e) => {
      // Left button only; ignore presses on controls inside the header.
      if (e.button !== 0) return;
      if (e.target.closest("button, input, select, textarea, a")) return;

      const handle = e.currentTarget;
      const { pointerId } = e;

      dragOrigin.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        posX: position.x,
        posY: position.y,
      };

      const onPointerMove = (moveEvent) => {
        if (!dragOrigin.current || moveEvent.pointerId !== pointerId) return;
        setPosition({
          x: dragOrigin.current.posX + (moveEvent.clientX - dragOrigin.current.mouseX),
          y: dragOrigin.current.posY + (moveEvent.clientY - dragOrigin.current.mouseY),
        });
      };

      const endDrag = (endEvent) => {
        if (endEvent && endEvent.pointerId !== pointerId) return;
        dragOrigin.current = null;
        handle.removeEventListener("pointermove", onPointerMove);
        handle.removeEventListener("pointerup", endDrag);
        handle.removeEventListener("pointercancel", endDrag);
        handle.removeEventListener("lostpointercapture", endDrag);
        try { handle.releasePointerCapture(pointerId); } catch { /* already released */ }
      };

      try { handle.setPointerCapture(pointerId); } catch { /* capture unsupported; listeners still end it */ }
      handle.addEventListener("pointermove", onPointerMove);
      handle.addEventListener("pointerup", endDrag);
      handle.addEventListener("pointercancel", endDrag);
      // Capture can be lost without a pointerup reaching the handle: the element is detached, a layer
      // above it turns off pointer events, or the browser drops it. Without this the drag never ends, so
      // moving the mouse over the header afterwards drags the dialog with no button held - which is the
      // failure the move to pointer events was meant to remove.
      handle.addEventListener("lostpointercapture", endDrag);

      e.preventDefault();
    },
    [position.x, position.y]
  );

  const resetPosition = useCallback(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  return { position, handlePointerDown, resetPosition };
}
